<template>
    <svg
        aria-hidden="true"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 300 300"
        class="blur-[2px] transition-[transform,opacity,filter] ease-in motion-reduce:transition-[opacity,filter]"
        :style="{ transform: `scale(${scaleLevel})` }"
        :class="{ speaking: isSpeaking, reconnecting: isReconnecting, muted: isMuted, setup: isSetup, thinking: isThinking, 'user-speaking': isUserSpeaking }">
        <defs>
            <filter id="displacement-filter" color-interpolation-filters="sRGB">
                <feImage id="displacement-map" :href="Marble" x="0" y="0" width="300" height="300" />
                <feDisplacementMap in="SourceGraphic" in2="displacementMap" scale="130" xChannelSelector="G" yChannelSelector="G" />
                <feGaussianBlur stdDeviation="15" />
            </filter>

            <pattern id="imagePattern" patternUnits="userSpaceOnUse" width="600" height="600">
                <image id="source-image" :href="gradient" width="600" height="600" preserveAspectRatio="xMidYMid slice" />
                <animateTransform
                    v-if="!prefersReducedMotion"
                    attributeName="patternTransform"
                    type="translate"
                    values="600,0;0,600"
                    dur="4s"
                    repeatCount="indefinite"
                    fill="freeze" />
            </pattern>

            <mask id="circleMask">
                <circle cx="150" cy="150" r="150" fill="white" />
            </mask>
        </defs>
        <rect width="300" height="300" fill="url(#imagePattern)" mask="url(#circleMask)" style="filter: url(#displacement-filter)" />
    </svg>
</template>

<script setup lang="ts">
import { useMediaQuery } from "@vueuse/core";
import gradient from "~img/gradient.jpg";
import Marble from "~img/realtime_chat.svg";
import { computed } from "vue";

const { state, level = 1.5 } = defineProps<{
    state: string;
    level?: number;
}>();

const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");

const isSpeaking = computed(() => state === "speaking");
const isReconnecting = computed(() => state === "reconnecting");
const isMuted = computed(() => state === "muted");
const isSetup = computed(() => state === "setup");
const isThinking = computed(() => state === "thinking");
const isUserSpeaking = computed(() => state === "user_speaking");

const MIN_THRESHOLD = 1.085;
const MAX_THRESHOLD = 1.25;
const scaleLevel = computed(() => {
    if (prefersReducedMotion.value) {
        return 1;
    }
    return level + 1 > MIN_THRESHOLD ? Math.min(level + 1, MAX_THRESHOLD) : 1;
});
</script>

<style scoped>
.speaking {
    animation: spin 4s linear infinite;
}

.reconnecting {
    opacity: 0.5;
    animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
    0%,
    100% {
        opacity: 0.5;
    }
    50% {
        opacity: 0.3;
    }
}

.muted {
    opacity: 0.6;
    filter: grayscale(0.3);
    transition:
        opacity 0.3s ease,
        filter 0.3s ease;
}

.setup {
    animation: breathe 3s ease-in-out infinite;
}

@keyframes breathe {
    0%,
    100% {
        transform: scale(0.95);
        opacity: 0.7;
    }
    50% {
        transform: scale(1.05);
        opacity: 1;
    }
}

.thinking {
    animation: think-pulse 2s ease-in-out infinite;
}

@keyframes think-pulse {
    0%,
    100% {
        transform: scale(1);
        opacity: 0.8;
    }
    50% {
        transform: scale(1.08);
        opacity: 1;
    }
}

.user-speaking {
    animation: glow 1.5s ease-in-out infinite;
}

@keyframes glow {
    0%,
    100% {
        filter: brightness(1);
    }
    50% {
        filter: brightness(1.15);
    }
}

@media (prefers-reduced-motion: reduce) {
    .speaking,
    .setup,
    .thinking {
        animation: none;
    }
}
</style>
